import { mapTour, mapTours } from '#api/tourservice/adapter/tourAdapter.ts';
import { schemas } from '#api/tourservice/model/__generated_api.ts';
import { validateWarn } from '#api/validateZodHelper.ts';
import type { Tour } from '#domain/model/Tour.ts';
import { apiTourService } from '#store/apiTourService.ts';

const url = '/frontend/tours';
const activeTourUrl = '/frontend/tours/active';
export const TOUR_SERVICE_API_LIMIT = 500;

type ToursQueryParams = {
    limit: number;
    asset_id?: string;
};

type ToursAfterQueryParams = ToursQueryParams & {
    cursor: string;
};

export type GetToursResponse = {
    tours: Tour[];
    cursor: string | undefined;
};

export type GetActiveTourResponse = {
    tour: Tour | undefined;
};

const getCursorFromNext = (next: string | undefined) => {
    if (!next) {
        return;
    }

    try {
        const parsedUrl = new URL(next);
        return parsedUrl.searchParams.get('cursor') ?? undefined;
    } catch {
        const queryStartIndex = next.indexOf('?');
        if (queryStartIndex >= 0) {
            const queryParams = new URLSearchParams(next.slice(queryStartIndex + 1));
            return queryParams.get('cursor') ?? undefined;
        }
    }

    return;
};

export const tourApiSlice = apiTourService.injectEndpoints({
    endpoints: build => ({
        getTourServiceTours: build.query<GetToursResponse, ToursQueryParams>({
            query: params => ({
                url,
                params,
            }),
            transformResponse: (response, meta, arg) => {
                const tours = validateWarn(schemas.TourCursor)(response, meta, arg);
                const mappedTours = mapTours(tours.items ?? []);
                const nextCursor = getCursorFromNext(tours.next);

                return {
                    tours: mappedTours,
                    cursor: nextCursor,
                };
            },
        }),
        getTourServiceToursAfter: build.query<GetToursResponse, ToursAfterQueryParams>({
            query: params => ({
                url,
                params,
            }),
            transformResponse: (response, meta, arg) => {
                const tours = validateWarn(schemas.TourCursor)(response, meta, arg);
                const mappedTours = mapTours(tours.items ?? []);
                const nextCursor = getCursorFromNext(tours.next);
                const cursorChanged = nextCursor !== arg.cursor;

                return {
                    tours: mappedTours,
                    cursor: cursorChanged ? nextCursor : undefined,
                };
            },
        }),
        getActiveTourServiceTour: build.query<GetActiveTourResponse, { asset_id: string }>({
            query: params => ({
                url: activeTourUrl,
                params,
                validateStatus: response => response.status === 200 || response.status === 204,
            }),
            transformResponse: (response, meta, arg) => {
                if (meta?.response?.status === 204) {
                    return {
                        tour: undefined,
                    };
                }

                const tour = validateWarn(schemas.Tour)(response, meta, arg);
                return {
                    tour: mapTour(tour),
                };
            },
        }),
    }),
});

export const { useGetActiveTourServiceTourQuery, useGetTourServiceToursAfterQuery, useGetTourServiceToursQuery } =
    tourApiSlice;
